export const student = {
  id: 'emmanuel',
  name: 'Emmanuel',
  grade: '7th',
  initials: 'EM'
};

export const householdChildren = [
  student,
  {
    id: 'sophia',
    name: 'Sophia',
    grade: '5th',
    initials: 'SO'
  }
];

export const topics = [
  {
    id: 'linear-equations',
    name: 'Solving for X',
    fullName: 'Solving for X in linear equations',
    subject: 'Math',
    description: 'Use inverse operations to find an unknown value.',
    starter: 'Can you show me how to solve 3x + 5 = 20?',
    color: 'coral'
  },
  {
    id: 'graphing-lines',
    name: 'Graphing Linear Equations',
    fullName: 'Graphing Linear Equations',
    subject: 'Math',
    description: 'Plot points and draw a line from a linear equation.',
    starter: 'How do I graph the equation y = 2x + 1?',
    color: 'coral'
  },
  {
    id: 'rock-cycle',
    name: 'The rock cycle',
    fullName: 'The rock cycle',
    subject: 'Science',
    description: 'See how rocks change between three main forms.',
    starter: 'How can one kind of rock turn into another?',
    color: 'sage'
  },
  {
    id: 'newtons-first-law',
    name: "Newton's First Law",
    fullName: "Newton's First Law",
    subject: 'Science',
    description: 'Understand inertia and changes in motion.',
    starter: 'Why does my body move forward when a car stops?',
    color: 'gold'
  }
];

export const topicResponses = {
  'linear-equations': {
    intro: "Great choice! We'll solve equations one small step at a time. Think of the equals sign as a balanced scale.",
    answer: "Let's solve 3x + 5 = 20 together. First, undo the +5 by subtracting 5 from both sides. That gives us 3x = 15. Then divide both sides by 3, so x = 5.",
    check: 'Quick check: if x is 5, then 3 × 5 + 5 = 20. It works! Which step would you like me to explain another way?'
  },
  'graphing-lines': {
    intro: "Nice, we're moving on to graphing! A linear equation can be drawn as a straight line once we know a couple of points.",
    answer: 'For y = 2x + 1, pick two x-values. When x = 0, y = 1, giving the point (0, 1). When x = 1, y = 3, giving (1, 3). Plot both points and draw a straight line through them.',
    check: 'The number in front of x (2) is the slope — for every 1 step right, the line moves up 2. The +1 is where the line crosses the y-axis. Want to try graphing a different equation?'
  },
  'rock-cycle': {
    intro: "Let's explore the rock cycle! Rocks are always changing, even though those changes can take a very long time.",
    answer: 'Heat and pressure can turn sedimentary or igneous rock into metamorphic rock. Melting makes magma, which cools into igneous rock. Weathering creates sediment that can become sedimentary rock.',
    check: 'There is no single starting point—it is a cycle. Want to trace what could happen to a piece of granite next?'
  },
  'newtons-first-law': {
    intro: "Let's make inertia feel less mysterious. Newton's First Law is really about objects resisting a change in motion.",
    answer: 'When a moving car stops, the car changes its motion but your body keeps moving forward for a moment. Your seat belt supplies the force that safely changes your motion too.',
    check: 'That tendency to keep doing what you were already doing is called inertia. Can you think of another example from sports or home?'
  }
};

export const initialCurriculum = [
  {
    subject: 'Math',
    lessons: [
      {
        id: 'linear-equations',
        order: 1,
        title: 'Solving for X',
        description: 'Use inverse operations to find an unknown value.',
        topicId: 'linear-equations',
        status: 'completed',
        progress: 100,
        assigned: true,
        sessionCount: 3,
        lastActivity: 'Today, 3:42 PM',
        activityOrder: 1,
        duration: '24 min'
      },
      {
        id: 'graphing-lines',
        order: 2,
        title: 'Graphing Linear Equations',
        description: 'Plot points and draw a line from a linear equation.',
        topicId: 'graphing-lines',
        status: 'available',
        progress: 0,
        assigned: true,
        sessionCount: 0,
        lastActivity: null,
        activityOrder: null,
        duration: null
      }
    ]
  },
  {
    subject: 'Science',
    lessons: [
      {
        id: 'rock-cycle',
        order: 1,
        title: 'The Rock Cycle',
        description: 'See how rocks change between three main forms.',
        topicId: 'rock-cycle',
        status: 'in-progress',
        progress: 55,
        assigned: true,
        sessionCount: 2,
        lastActivity: 'Yesterday, 11:10 AM',
        activityOrder: 2,
        duration: '17 min'
      },
      {
        id: 'newtons-first-law',
        order: 2,
        title: "Newton's First Law",
        description: 'Understand inertia and changes in motion.',
        topicId: 'newtons-first-law',
        status: 'locked',
        progress: 0,
        assigned: true,
        sessionCount: 0,
        lastActivity: null,
        activityOrder: null,
        duration: null
      }
    ]
  }
];

export const sophiaCurriculum = [
  {
    subject: 'Math',
    lessons: [
      {
        id: 'sophia-fractions',
        order: 1,
        title: 'Fractions as Division',
        description: 'Connect fractions to division and equal sharing.',
        status: 'completed',
        progress: 100,
        assigned: true,
        sessionCount: 2,
        lastActivity: 'Monday, 2:20 PM',
        activityOrder: 1,
        duration: '19 min'
      },
      {
        id: 'sophia-area-perimeter',
        order: 2,
        title: 'Area and Perimeter',
        description: 'Measure and compare the space around and inside shapes.',
        status: 'available',
        progress: 0,
        assigned: true,
        sessionCount: 0,
        lastActivity: null,
        activityOrder: null,
        duration: null
      }
    ]
  },
  {
    subject: 'Science',
    lessons: [
      {
        id: 'sophia-ecosystems',
        order: 1,
        title: 'Living in Ecosystems',
        description: 'Explore how organisms interact with their environment.',
        status: 'in-progress',
        progress: 35,
        assigned: true,
        sessionCount: 1,
        lastActivity: 'Friday, 10:05 AM',
        activityOrder: 2,
        duration: '12 min'
      },
      {
        id: 'sophia-food-chains',
        order: 2,
        title: 'Food Chains and Webs',
        description: 'Follow how energy moves through an ecosystem.',
        status: 'locked',
        progress: 0,
        assigned: false,
        sessionCount: 0,
        lastActivity: null,
        activityOrder: null,
        duration: null
      }
    ]
  }
];

export const initialCurriculaByChild = {
  emmanuel: initialCurriculum,
  sophia: sophiaCurriculum
};